import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { equipmentStatuses, triggerTypes } from "../../../../enums";
import { useFetch } from "../../../../hooks/useFetch";
import { SelectOption, Sensor, TriggerType } from "../../../../types/types";
import { Button } from "../../../../ui/Button";
import { Input } from "../../../../ui/Input/Input";
import { Label } from "../../../../ui/Label/Label";
import { ScrollArea } from "../../../../ui/ScrollArea/ScrollArea";
import { SelectButton } from "../../../../ui/SelectButton/SelectButton";
import { Switch, SwitchSM } from "../../../../ui/Switch/Switch";
import { TriggersContext } from "./Triggers";
import { useAppStore } from "../../../../stores/RootStore";

export interface RequestDataType {
  name: string;
  type?: string;
  min_triggered_period: number;
  enabled: boolean;
  sensor_id: number;
  status_change_to: string;
  string_value_equal?: string;
  value_less_than?: number;
  value_more_than?: number;
}

type Props = {
  closeEditModal: Function;
};

const AddTrigger = ({ closeEditModal }: Props) => {
  const store = useAppStore()
  const { triggers, setTriggers, equipment } = useContext(TriggersContext)!;

  const statusChanged = equipmentStatuses.map((status) => {
    return { value: status, label: t(status) };
  });
  const statusChangedToList = [{ value: "", label: t("do_not_change_status") }].concat(statusChanged);

  const [sensorList, setSensorList] = useState<SelectOption[]>([]);

  const [errors, setErrors] = useState([]);
  const { reset, handleSubmit, control, watch, setValue } = useForm({
    defaultValues: {
      name: "",
      type: null,
      enabled: true,
      min_triggered_period: 10,
      sensor_id: "",
      status_change_to: null,
      value_more_than: "",
      value_less_than: "",
      card: "",
      equipment: equipment.id,
      sensor: null,
    },
  });

  const [{ response, isLoading, error }, doFetchSensors] = useFetch(
    `/directories/equipment_items/${watch("equipment")}/sensors`
  );

  useEffect(() => {
    if (!response) return;
    const responseData = response.data as unknown as Sensor[];
    setSensorList(
      responseData.map((sensor) => {
        return { value: String(sensor.id), label: String(sensor.attributes.sensor_type.long_name) };
      })
    );
  }, [response]);

  useEffect(() => {
    const chosenEquipmentId = watch("equipment");
    setValue("sensor", null);
    if (!chosenEquipmentId) return;
    doFetchSensors();
  }, [watch("equipment")]);

  const isTypeIs = (type: "rfid" | "value_less" | "value_more") => {
    const typeValue = watch("type");
    switch (type) {
      case "rfid":
        const isRfid = typeValue == "rfid_in" || typeValue == "rfid_out" || typeValue == "rfid_switch";
        return isRfid;

      case "value_less":
        const isValueLess = typeValue == "value_less_than";
        return isValueLess;

      case "value_more":
        const isValueMore = typeValue == "value_more_than";
        return isValueMore;
    }
  };

  const [{ response: saveResponse, isLoading: saveIsLoading, error: saveError }, doFetchItemSave] =
    useFetch(`/directories/triggers`);

  let requestData: RequestDataType;
  const onSubmit = (formData: any) => {
    setErrors([]);

    requestData = {
      name: formData.name,
      min_triggered_period: Number(formData.min_triggered_period),
      enabled: formData.enabled,
      sensor_id: Number(formData.sensor),
      status_change_to: formData.status_change_to,
    };

    switch (formData.type) {
      //@ts-ignore
      case "rfid_switch":
        if (!requestData.type) requestData.type = "Sites::Triggers::RfidSwitch";
      //@ts-ignore
      case "rfid_out":
        if (!requestData.type) requestData.type = "Sites::Triggers::RfidOut";
      case "rfid_in":
        if (!requestData.type) requestData.type = "Sites::Triggers::RfidIn";
        requestData.string_value_equal = formData.card;
        break;
      case "value_less_than":
        requestData.type = "Sites::Triggers::ValueLessThan";
        requestData.value_less_than = Number(formData.value_less_than);
        break;
      case "value_more_than":
        requestData.type = "Sites::Triggers::ValueMoreThan";
        requestData.value_more_than = Number(formData.value_more_than);
        break;
    }

    doFetchItemSave({
      method: "POST",
      data: {
        data: requestData,
      },
    });
  };

  const addTrigger = (itemSaveResponse: { data: unknown }) => {
    const newScreenData = itemSaveResponse.data as unknown as TriggerType;
    let newTriggers = [...triggers!, newScreenData]
    setTriggers(newTriggers);
    store.setTriggers(newTriggers)
  };

  useEffect(() => {
    if (!saveResponse) return;
    addTrigger(saveResponse);
    reset();
    closeEditModal();
  }, [saveResponse]);

  useEffect(() => {
    if (!saveError) return;
    setErrors(saveError.errors);
    console.log(saveError);
  }, [saveError]);

  return (
    <ScrollArea className="w-full">
      <div className="flex flex-col w-full">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col relative space-y-1">
              <Controller
                name="enabled"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <div className="flex justify-between items-center py-2">
                      <Label htmlFor="enabled">{field.value ? t("trigger_enabled") : t("trigger_disabled")}</Label>
                      <SwitchSM checked={field.value} onCheckedChange={field.onChange} id="enabled" />
                    </div>
                  </div>
                )}
              />
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Input {...field} label={`${t("trigger_name")}*`} />
                  </div>
                )}
              />
              <Controller
                name="type"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("trigger_type")}*</Label>
                    <SelectButton
                      label={"Выберите тип триггера"}
                      searchable={false}
                      options={triggerTypes.filter(tt => tt.group === "equipment")}
                      value={value ? triggerTypes?.filter(tt => tt.group === "equipment").find((s) => s.value == String(value))! : null}
                      onChange={(e) => onChange(e?.value)}
                    />
                  </div>
                )}
              />
              <Controller
                name="min_triggered_period"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Input type="number" {...field} label={`${t("min_triggered_period")}*`} />
                  </div>
                )}
              />
              <Controller
                name="sensor"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("sensor")}*</Label>
                    <SelectButton
                      disabled={watch("equipment") ? false : true}
                      label={"Выберите датчик"}
                      searchable={false}
                      options={sensorList}
                      value={value ? sensorList?.find((s) => s.value == String(value))! : null}
                      onChange={(e) => onChange(e?.value)}
                    />
                  </div>
                )}
              />
              {isTypeIs("value_more") && (
                <Controller
                  name="value_more_than"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div className="relative space-y-2">
                      <Input type="number" {...field} label={`${t("value_more_than")}*`} />
                    </div>
                  )}
                />
              )}
              {isTypeIs("value_less") && (
                <Controller
                  name="value_less_than"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div className="relative space-y-2">
                      <Input type="number" {...field} label={`${t("value_less_than")}*`} />
                    </div>
                  )}
                />
              )}
              {isTypeIs("rfid") && (
                <Controller
                  name="card"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div className="relative space-y-2">
                      <Input {...field} label={`${t("card")}*`} />
                    </div>
                  )}
                />
              )}
              <Controller
                name="status_change_to"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("status_change_to")}*</Label>
                    <SelectButton
                      label={t("do_not_change_status")}
                      searchable={false}
                      options={statusChangedToList}
                      value={value ? statusChangedToList?.find((s) => s.value == String(value))! : null}
                      onChange={(e) => onChange(e?.value)}
                    />
                  </div>
                )}
              />
            </div>
          </div>
          <div className="mt-8 flex w-full justify-end">
            <Button type="submit" className="mt-8">
              {t("save")}
            </Button>
          </div>
        </form>
      </div>
    </ScrollArea>
  );
};

export default AddTrigger;
